import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { configService } from '../services/configService'
import type { ProcessDailyResponse } from '../types'

const POLL_INTERVAL_MS = 2000
// 20 minutes — observed worst-case run is ~16 min against the demo dataset.
const POLL_TIMEOUT_MS = 20 * 60 * 1000

const FINAL_STATES = new Set(['success', 'partial', 'error'])

export function useProcessDaily() {
  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<ProcessDailyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const cancelledRef = useRef(false)

  useEffect(() => {
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const run = async () => {
    setIsRunning(true)
    setError(null)
    setResult(null)
    try {
      const started = await configService.runProcessDaily()
      const id = started.id_execution
      const deadline = Date.now() + POLL_TIMEOUT_MS

      // Poll the cron log until the BackgroundTask finishes.
      // The HTTP layer returns immediately; the work runs in the same
      // process but off the request thread.
      while (!cancelledRef.current) {
        if (Date.now() > deadline) {
          throw new Error(
            `Timeout esperando que el proceso diario (id=${id}) termine`,
          )
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
        const log = await configService.getCronLog(id)
        if (FINAL_STATES.has(log.status)) {
          if (log.status === 'error' && !log.result) {
            throw new Error(log.error_message || 'Proceso diario falló')
          }
          if (log.result) {
            setResult(log.result)
          }
          break
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        setError(typeof detail === 'string' ? detail : 'Error al ejecutar el proceso diario')
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Error al ejecutar el proceso diario')
      }
    } finally {
      setIsRunning(false)
    }
  }

  const reset = () => {
    setResult(null)
    setError(null)
  }

  return { run, isRunning, result, error, reset }
}
